/**
 * _unsaved_work.js — one page-wide guard for edits that would be lost by leaving.
 *
 * Forms register a `hasUnsavedWork()` callback while connected. Whatever the
 * number of registered forms, the page asks once, and only when leaving would
 * actually lose something:
 *
 *   turbo:before-visit  a link tap or Turbo.visit. Listened in the CAPTURE phase
 *                       so it decides before anything else reacts to the visit:
 *                       skeleton-nav.js wipes the page into the destination's
 *                       skeleton on this same event, so a guard that ran after it
 *                       could cancel the visit yet leave the user staring at a
 *                       skeleton with their edits gone. A cancelled visit also
 *                       stops immediate propagation, so no later listener sees
 *                       the visit at all. That includes page-check.js, whose own
 *                       swap is a Turbo.visit to this URL: its userIsBusy asks
 *                       this registry (window.chippieUnsavedWork.any) and holds
 *                       the swap, so the swap never raises this prompt.
 *   Back/Forward        Turbo dispatches no before-visit for a history pop.
 *     navigate          Where the Navigation API exists (Chromium, WebKit, recent
 *                       Firefox) its `navigate` event fires before the step is
 *                       taken, and cancelling it means the step never happens.
 *     popstate          Fallback: listens on window in the capture phase, ahead
 *                       of Turbo's own handler, and staying undoes the step
 *                       (direction from Turbo's restorationIndex), hiding both the
 *                       pop and its undo from Turbo. This only works where capture
 *                       listeners at the target run first, per spec: Chromium runs
 *                       window listeners in registration order, so Turbo's earlier
 *                       one would win there. That is why navigate is preferred.
 *   beforeunload        a full browser navigation, reload or tab close: the
 *                       browser's own prompt.
 *
 * It also marks the tab (Bootstrap `[data-bs-target="#pane"]`) of every tab pane
 * holding unsaved work, so an edit left on another tab stays visible. Switching
 * tabs is not guarded: the edit stays in the form, so nothing is lost by it.
 *
 * Callers report every state change with `unsavedWorkChanged()`.
 * Styles: static/css/settings.css (.has-unsaved).
 */

const LEAVE_PROMPT = 'You have unsaved changes. Leave without saving?';
const registrants = new Map();   // element -> hasUnsavedWork()
let undoingPop = false;

export function guardUnsavedWork(element, hasUnsavedWork) {
    if (!registrants.size) listen();
    registrants.set(element, hasUnsavedWork);
}

export function releaseUnsavedWork(element) {
    registrants.delete(element);
    if (!registrants.size) unlisten();
    unsavedWorkChanged();
}

export function unsavedWorkChanged() {
    const unsavedPanes = new Set();
    registrants.forEach((hasUnsavedWork, element) => {
        const pane = element.closest('.tab-pane');
        if (pane && hasUnsavedWork()) unsavedPanes.add(pane.id);
    });
    document.querySelectorAll('[data-bs-target^="#"]').forEach((tab) => {
        const paneId = tab.getAttribute('data-bs-target').slice(1);
        if (document.getElementById(paneId)?.classList.contains('tab-pane')) {
            tab.classList.toggle('has-unsaved', unsavedPanes.has(paneId));
        }
    });
}

function anyUnsavedWork() {
    for (const hasUnsavedWork of registrants.values()) {
        if (hasUnsavedWork()) return true;
    }
    return false;
}

// page-check.js is a classic script outside this bundle, so it asks through window.
window.chippieUnsavedWork = { any: anyUnsavedWork };

function onBeforeVisit(event) {
    if (event.defaultPrevented || !anyUnsavedWork()) return;
    if (window.confirm(LEAVE_PROMPT)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
}

// A cross-document traversal is not cancelable here; beforeunload covers it.
function onNavigate(event) {
    if (event.navigationType !== 'traverse' || !event.cancelable) return;
    if (!anyUnsavedWork() || window.confirm(LEAVE_PROMPT)) return;
    event.preventDefault();
}

function onPopState(event) {
    if (undoingPop) {
        undoingPop = false;
        event.stopImmediatePropagation();
        return;
    }
    if (!anyUnsavedWork() || window.confirm(LEAVE_PROMPT)) return;
    event.stopImmediatePropagation();
    undoingPop = true;
    history.go(poppedForward(event) ? -1 : 1);
    // history.go is async and a no-op at either end of the stack; never let a
    // stale flag swallow the user's next real Back.
    setTimeout(() => { undoingPop = false; }, 1000);
}

// Turbo stamps each entry with restorationIndex and remembers the index in view.
function poppedForward(event) {
    const popped = event.state?.turbo?.restorationIndex;
    const current = window.Turbo?.session?.history?.currentIndex;
    return typeof popped === 'number' && typeof current === 'number' && popped > current;
}

function onBeforeUnload(event) {
    if (!anyUnsavedWork()) return;
    event.preventDefault();
    event.returnValue = '';   // older engines show the prompt only for a set returnValue
}

function listen() {
    document.addEventListener('turbo:before-visit', onBeforeVisit, { capture: true });
    if (window.navigation) {
        window.navigation.addEventListener('navigate', onNavigate);
    } else {
        window.addEventListener('popstate', onPopState, { capture: true });
    }
    window.addEventListener('beforeunload', onBeforeUnload);
}

function unlisten() {
    document.removeEventListener('turbo:before-visit', onBeforeVisit, { capture: true });
    if (window.navigation) {
        window.navigation.removeEventListener('navigate', onNavigate);
    } else {
        window.removeEventListener('popstate', onPopState, { capture: true });
    }
    window.removeEventListener('beforeunload', onBeforeUnload);
}
